// Provides a global this.$user to Vue components that contains:
// id - string
// profile: {
//   name - string
//   email - string
// }
// isEmployee - boolean
// hasValidSession - boolean

import { reactive, readonly, type App } from 'vue'
import {
  getUserId,
  getUserProfile,
  hasValidUserSession,
  isEmployee,
  onUserLoaded,
  onUserUnloaded,
  onAccessTokenExpired,
} from '@/services/identity-client'
import { setUser } from '@sentry/vue'

let _app: App | undefined

/**
 * *(AI-generated)*
 * Initializes the Vue app user and sets up identity event listeners to keep user state current.
 */
// eslint-disable-next-line require-await
export async function provideVueAppUser(app: App) {
  _app = app
  updateVueUser()

  // Update user for identity when user is added
  onUserLoaded(updateVueUser)
  onUserUnloaded(updateVueUser)
  onAccessTokenExpired(updateVueUser)
}

interface UserState {
  id: string | null | undefined
  profile: { name: string; email: string }
  isEmployee: boolean
  hasValidSession: boolean
  hasUserSessionExpired: boolean
}

const reactiveUser = reactive<UserState>({
  id: null,
  profile: { name: '', email: '' },
  isEmployee: false,
  hasValidSession: false,
  hasUserSessionExpired: false,
})

async function updateVueUser() {
  if (_app) {
    const [id, profile, employee, validSession] = await Promise.all([
      getUserId(),
      getUserProfile(),
      isEmployee(),
      hasValidUserSession(),
    ])
    setUser({
      id,
    })
    reactiveUser.id = id
    if (profile) {
      reactiveUser.profile = {
        name: profile.name ?? '',
        email: profile.email ?? '',
      }
    }
    reactiveUser.isEmployee = employee ?? false
    reactiveUser.hasValidSession = validSession
    reactiveUser.hasUserSessionExpired = !!id && !validSession
    if (!_app.config.globalProperties.$user) {
      _app.config.globalProperties.$user = reactiveUser
    }
  }
}

/**
 * *(AI-generated)*
 * Returns a readonly reactive reference to the current user state.
 */
export function useUser() {
  return readonly(reactiveUser)
}
